import React, { useState, useEffect, useRef } from "react";
import api from "../api/api";

export default function CustomerModal({ customer, onClose, onVerify }) {
  const [editData, setEditData] = useState({
    name: customer.Vname || customer.name || "",
    email: customer.Mail || customer.email || "",
    mobile: customer.Vcode || customer.mobile || "",
    address: customer.Address1 || customer.address || "",
    area: customer.City || customer.area || "",
    landmark: customer.Address2 || customer.landmark || "",
    pincode: customer.Pincode || customer.pincode || "",
    division: customer.Division || customer.division || "",
  });

  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [areaLoading, setAreaLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState(editData.area || "");
  const [showAreaList, setShowAreaList] = useState(false);
  const areaRef = useRef(null);

  useEffect(() => {
    setSearchTerm(editData.area || "");
  }, [customer]);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = (await api.GetAreaList?.()) || (await api.get("GetAreaList", {}));
        const list =
          response?.data?.GetAreaListjs?.Table ||
          response?.GetAreaListjs?.Table ||
          response?.data?.Table ||
          [];
        setAreas(list);

        // Auto-fill division if City matches
        const matched = list.find(
          (a) => a.city === (customer.City || customer.area)
        );
        if (matched) {
          setEditData((prev) => ({ ...prev, division: matched.Division }));
        }
      } catch (error) {
        console.error("❌ Error fetching areas:", error);
      } finally {
        setAreaLoading(false);
      }
    };
    fetchAreas();
  }, [customer]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (areaRef.current && !areaRef.current.contains(e.target)) {
        setShowAreaList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Generic input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "area") {
      const selected = areas.find((a) => a.city === value);
      setEditData({
        ...editData,
        area: value,
        division: selected ? selected.Division : "",
      });
      setSearchTerm(value);
    } else {
      setEditData({ ...editData, [name]: value });
    }
  };

  // When user selects an area from filtered dropdown
  const handleSelectArea = (city) => {
    const selected = areas.find((a) => a.city === city);
    setEditData({
      ...editData,
      area: city,
      division: selected ? selected.Division : "",
    });
    setSearchTerm(city);
    setShowAreaList(false);
  };

  // Filter areas dynamically
  const filteredAreas = areas.filter((a) =>
    a.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Verify customer API
  const handleVerify = async (status) => {
    if (!status) {
      onVerify?.(customer.id, false);
      onClose();
      return;
    }

    setLoading(true);
    const payload = {
      Items: [
        {
          Vid: customer.id,
          Vcode: editData.mobile || "NA",
          Vname: editData.name || "NA",
          Mail: editData.email || "NA",
          Address1: editData.address || "NA",
          Address2: editData.landmark || "NA",
          City: editData.area || "NA",
          Division: editData.division || "NA",
          Pincode: editData.pincode || "000000",
        },
      ],
      UserType: "New User",
    };


    try {
      const response = await api.VerifyCustomerAPI(payload);
      console.log("✅ VerifyCustomerAPI Response:", response.data);
      onVerify?.(customer.id, true);
      onClose();
      
       window.location.reload();

    } catch (error) {
      console.error("❌ Error verifying customer:", error);
      alert("Failed to verify customer. Please check console.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-3 bg-black bg-opacity-50">
      <div className="bg-white p-5 rounded-xl w-full max-w-lg shadow-lg overflow-y-auto max-h-[90vh]">
        <h2 className="mb-3 text-lg font-bold text-center text-orange-600 sm:text-xl">
          Edit & Verify Customer
        </h2>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Full Name */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700">Full Name</label>
            <input
              type="text"
              name="name"
              value={editData.name}
              onChange={handleChange}
              className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
            />
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700">Email ID</label>
            <input
              type="email"
              name="email"
              value={editData.email}
              onChange={handleChange}
              className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
            />
          </div>

          {/* Mobile */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700">Mobile Number</label>
            <input
              type="text"
              name="mobile"
              value={editData.mobile}
              onChange={handleChange}
              className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
            />
          </div>

          {/* Pincode */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700">Pincode</label>
            <input
              type="text"
              name="pincode"
              value={editData.pincode}
              onChange={handleChange}
              className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
            />
          </div>

          {/* Address */}
          <div className="flex flex-col sm:col-span-2">
            <label className="text-xs font-semibold text-gray-700">Delivery Address</label>
            <textarea
              name="address"
              value={editData.address}
              onChange={handleChange}
              rows="2"
              className="p-2 text-sm border rounded-md resize-none focus:ring-1 focus:ring-orange-400"
            ></textarea>
          </div>

          {/* ✅ Searchable Area */}
          <div className="flex flex-col relative" ref={areaRef}>
            <label className="text-xs font-semibold text-gray-700 mb-1">Area</label>
            <input
              type="text"
              name="area"
              placeholder={areaLoading ? "Loading areas..." : "Search area..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowAreaList(true)}
              className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
              autoComplete="off"
              disabled={areaLoading}
            />

            {showAreaList && (
              <ul className="absolute top-full left-0 w-full max-h-40 mt-1 overflow-y-auto bg-white border rounded-md shadow-md z-10">
                {filteredAreas.length > 0 ? (
                  filteredAreas.map((item, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelectArea(item.city)}
                      className="p-2 text-sm cursor-pointer hover:bg-orange-100"
                    >
                      {item.city}
                    </li>
                  ))
                ) : (
                  <li className="p-2 text-sm text-gray-500">No results found</li>
                )}
              </ul>
            )}
          </div>

          {/* Landmark */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700">Landmark</label>
            <input
              type="text"
              name="landmark"
              value={editData.landmark}
              onChange={handleChange}
              className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-wrap justify-end gap-1 mt-5 sm:gap-2">
          <button
            onClick={() => handleVerify(true)}
            disabled={loading}
            className="w-auto px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
          <button
            onClick={() => handleVerify(false)}
            disabled={loading}
            className="w-auto px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            Not Verified
          </button>
          <button
            onClick={onClose}
            className="w-auto px-4 py-2 text-sm text-gray-800 bg-gray-300 rounded-lg hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


// import React, { useState, useEffect } from "react";
// import api from "../api/api"; // Ensure api instance is correct

// export default function CustomerModal({ customer, onClose, onVerify }) {
//   const [editData, setEditData] = useState({
//     name: customer.name || "",
//     email: customer.email || "",
//     mobile: customer.mobile || "",
//     address: customer.address || "",
//     area: customer.area || "",
//     landmark: customer.landmark || "",
//     pincode: customer.pincode || "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [areas, setAreas] = useState([]); // ✅ For dropdown options
//   const [areaLoading, setAreaLoading] = useState(true);

//   // ✅ Fetch Area List on mount
//   useEffect(() => {
//     const fetchAreas = async () => {
//       try {
//         const response = await api.GetAreaList?.() || await api.get("GetAreaList", {});

//         // ✅ Detect correct JSON path automatically
//         const list =
//           response?.data?.GetAreaListjs?.Table ||
//           response?.GetAreaListjs?.Table ||
//           response?.data?.Table ||
//           [];

//         console.log("✅ Extracted Area List:", list);
//         setAreas(list);
//       } catch (error) {
//         console.error("❌ Error fetching area list:", error);
//       } finally {
//         setAreaLoading(false);
//       }
//     };

//     fetchAreas();
//   }, []);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setEditData({ ...editData, [name]: value });
//   };

//   // ✅ Verify customer API call
//   const handleVerify = async (status) => {
//     if (!status) {
//       if (onVerify) onVerify(customer.id, false);
//       onClose();
//       return;
//     }

//     setLoading(true);

//     const payload = {
//       Items: [
//         {
//           Vid: customer.id,
//           Vcode: customer.mobile || "NA",
//           Vname: editData.name || "NA",
//           Mail: editData.email || "NA",
//           Address1: editData.address || "NA",
//           Address2: editData.landmark || "NA",
//           City: editData.area || "NA",
//           division: editData.division,
//           Pincode: editData.pincode || "000000",
//         },
//       ],
//       UserType: "New User",
//     };

//     console.log("📦 Payload to VerifyCustomerAPI:", JSON.stringify(payload, null, 2));

//     try {
//       const response = await api.VerifyCustomerAPI(payload);
//       console.log("✅ VerifyCustomerAPI Response:", response.data);

//       if (onVerify) onVerify(customer.id, true);
//       onClose();
//       // window.location.reload();
//     } catch (error) {
//       console.error("❌ Error verifying customer:", error);
//       alert("Failed to verify customer. Please check console for details.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center px-3 bg-black bg-opacity-50">
//       <div className="bg-white p-5 rounded-xl w-full max-w-lg shadow-lg overflow-y-auto max-h-[90vh]">
//         <h2 className="mb-3 text-lg font-bold text-center text-orange-600 sm:text-xl">
//           Edit & Verify Customer
//         </h2>

//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//           {/* Name */}
//           <div className="flex flex-col">
//             <label className="text-xs font-semibold text-gray-700">Full Name</label>
//             <input
//               type="text"
//               name="name"
//               value={editData.name}
//               onChange={handleChange}
//               className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
//             />
//           </div>

//           {/* Email */}
//           <div className="flex flex-col">
//             <label className="text-xs font-semibold text-gray-700">Email ID</label>
//             <input
//               type="email"
//               name="email"
//               value={editData.email}
//               onChange={handleChange}
//               className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
//             />
//           </div>

//           {/* Mobile */}
//           <div className="flex flex-col">
//             <label className="text-xs font-semibold text-gray-700">Mobile Number</label>
//             <input
//               type="text"
//               name="mobile"
//               value={editData.mobile}
//               onChange={handleChange}
//               className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
//             />
//           </div>

//           {/* Pincode */}
//           <div className="flex flex-col">
//             <label className="text-xs font-semibold text-gray-700">Pincode</label>
//             <input
//               type="text"
//               name="pincode"
//               value={editData.pincode}
//               onChange={handleChange}
//               className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
//             />
//           </div>

//           {/* Address */}
//           <div className="flex flex-col sm:col-span-2">
//             <label className="text-xs font-semibold text-gray-700">Delivery Address</label>
//             <textarea
//               name="address"
//               value={editData.address}
//               onChange={handleChange}
//               rows="2"
//               className="p-2 text-sm border rounded-md resize-none focus:ring-1 focus:ring-orange-400"
//             ></textarea>
//           </div>

//           {/* ✅ Area Dropdown */}
//           <div className="flex flex-col">
//             <label className="text-xs font-semibold text-gray-700">Area</label>
//             <select
//               name="area"
//               value={editData.area}
//               onChange={handleChange}
//               disabled={areaLoading}
//               className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
//             >
//               <option value="">-- Select Area --</option>
//               {areas.map((item, index) => (
//                 <option key={index} value={item.city}>
//                   {item.city}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Landmark */}
//           <div className="flex flex-col">
//             <label className="text-xs font-semibold text-gray-700">Landmark</label>
//             <input
//               type="text"
//               name="landmark"
//               value={editData.landmark}
//               onChange={handleChange}
//               className="p-2 text-sm border rounded-md focus:ring-1 focus:ring-orange-400"
//             />
//           </div>
//         </div>

//         {/* Buttons */}
//         <div className="flex flex-wrap justify-end gap-1 mt-5 sm:gap-2">
//           <button
//             onClick={() => handleVerify(true)}
//             disabled={loading}
//             className="w-auto px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
//           >
//             {loading ? "Verifying..." : "Verify"}
//           </button>
//           <button
//             onClick={() => handleVerify(false)}
//             disabled={loading}
//             className="w-auto px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
//           >
//             Not Verified
//           </button>
//           <button
//             onClick={onClose}
//             className="w-auto px-4 py-2 text-sm text-gray-800 bg-gray-300 rounded-lg hover:bg-gray-400"
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }
